import { useCallback, useMemo } from 'react';
import { createFileRoute, Outlet, useRouter } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { EntityIndexLayout, WorkspaceShell, useWorkspaceHeader } from '@/components/layouts';
import {
  ProposalListPanel,
  type ProposalListFilters,
} from '@/components/organisms/proposals/ProposalListPanel';
import { ProposalTableView } from '@/components/organisms/proposals/ProposalTableView';
import { useEntityRouteState } from '@/hooks/navigation/useEntityRouteState';
import { useIsMobile } from '@/hooks/useIsMobile';
import type { ProposalStatus } from '@/types/proposal';

export const Route = createFileRoute('/_dashboard/dashboard/proposals')({
  validateSearch: (search): ProposalListFilters => ({
    q: typeof search.q === 'string' ? search.q : '',
    status: (search.status as ProposalStatus | 'all') ?? 'all',
    proposalNumber: typeof search.proposalNumber === 'string' ? search.proposalNumber : '',
    updatedFrom: typeof search.updatedFrom === 'string' ? search.updatedFrom : undefined,
    updatedTo: typeof search.updatedTo === 'string' ? search.updatedTo : undefined,
    serviceAddress: typeof search.serviceAddress === 'string' ? search.serviceAddress : '',
    customerEmail: typeof search.customerEmail === 'string' ? search.customerEmail : '',
    customerPhone: typeof search.customerPhone === 'string' ? search.customerPhone : '',
    page: typeof search.page === 'number' && search.page > 0 ? Math.floor(search.page) : 1,
    sort: typeof search.sort === 'number' ? Math.floor(search.sort) : 0,
    dateFrom: typeof search.dateFrom === 'string' ? search.dateFrom : undefined,
    dateTo: typeof search.dateTo === 'string' ? search.dateTo : undefined,
  }),
  component: ProposalsLayout,
});

function cleanProposalSearch(next: Record<string, unknown>) {
  const defaults: Partial<Record<keyof ProposalListFilters, unknown>> = {
    q: '',
    status: 'all',
    proposalNumber: '',
    serviceAddress: '',
    customerEmail: '',
    customerPhone: '',
    page: 1,
    sort: 0,
  };

  (Object.keys(next) as Array<keyof ProposalListFilters>).forEach((key) => {
    if (next[key] == null || next[key] === defaults[key]) delete next[key];
  });

  return next as unknown as ProposalListFilters;
}

function ProposalsLayout() {
  const router = useRouter();
  const isMobile = useIsMobile();
  const filters = Route.useSearch();
  const {
    selectedId,
    isFormMode,
    hasChildRoute,
    isIndexRoute,
    preservedSearch: listSearch,
  } = useEntityRouteState({
    basePath: '/dashboard/proposals',
    search: filters,
    cleanSearch: cleanProposalSearch,
  });

  function handleSelect(id: string) {
    void router.navigate({
      to: '/dashboard/proposals/$id',
      params: { id },
      search: listSearch as never,
    });
  }

  function handleFiltersChange(patch: Partial<ProposalListFilters>) {
    void router.navigate({
      search: (prev) => {
        const next = { ...prev, ...patch } as Record<string, unknown>;
        return cleanProposalSearch(next) as never;
      },
      replace: true,
    });
  }

  const handleNewClick = useCallback(() => {
    void router.navigate({ to: '/dashboard/proposals/new', search: listSearch });
  }, [router, listSearch]);

  const listPanel = (
    <ProposalListPanel
      selectedId={selectedId}
      onSelect={handleSelect}
      compact
      filters={filters}
      onFiltersChange={handleFiltersChange}
    />
  );

  const mobileListPanel = (
    <ProposalListPanel
      onSelect={handleSelect}
      selectedId={selectedId}
      filters={filters}
      onFiltersChange={handleFiltersChange}
    />
  );

  return (
    <WorkspaceShell hideHeader={isFormMode}>
      <ProposalsNewButtonSlot
        hidden={isFormMode || (isMobile && hasChildRoute)}
        onClick={handleNewClick}
      />
      <EntityIndexLayout
        title="Proposals"
        storageKey="proposals-view"
        hasChildRoute={hasChildRoute}
        onResetToIndex={() =>
          void router.navigate({ to: '/dashboard/proposals', search: listSearch })
        }
        splitWidthClass="w-[360px]"
        tableSheetWidthClass={
          isFormMode ? 'w-[95vw] sm:max-w-[1200px]' : 'w-[620px] sm:max-w-[620px]'
        }
        viewToggleShowLabels
        viewToggleActiveTone="muted"
        hideListInSplit={isFormMode}
        splitList={() => <div className="flex h-full flex-col">{listPanel}</div>}
        splitDetail={<Outlet />}
        tableView={({ openInSheet }) => (
          <>
            <ProposalTableView
              onOpen={(id) =>
                openInSheet(
                  () =>
                    void router.navigate({
                      to: '/dashboard/proposals/$id',
                      params: { id },
                      search: listSearch as never,
                    }),
                )
              }
            />
          </>
        )}
        sheetContent={<Outlet />}
        mobileContent={
          <div className="md:hidden flex flex-col flex-1 min-h-0">
            {isIndexRoute ? <>{mobileListPanel}</> : <Outlet />}
          </div>
        }
      />
    </WorkspaceShell>
  );
}

function ProposalsNewButtonSlot({ hidden, onClick }: { hidden: boolean; onClick: () => void }) {
  const newButton = useMemo(() => {
    if (hidden) return null;
    return (
      <Button size="sm" onClick={onClick}>
        <Plus size={14} className="mr-1.5" />
        Add proposal
      </Button>
    );
  }, [hidden, onClick]);

  useWorkspaceHeader({ newButton }, [newButton]);
  return null;
}
